// animalAddendumClient — client for the WEL-153 Animal Addendum (a current
// tenant adding a new animal). Spec: docs/animal-addendum.md.
//
// Two surfaces in one module, the split lib/moveoutFundsClient.ts makes:
//
//   • AUTHENTICATED staff (Bearer via `api`) — the Send page at
//     /send/animal-addendum.
//       - GET  /api/animal-addendum/context?propertyId=&unitId=  → AnimalAddendumContext
//       - POST /api/animal-addendum/:occupancyId/initiate         → InitiateAnimalAddendumResult
//       - POST /api/animal-addendum/:occupancyId/cancel           → CancelAnimalAddendumResult
//       - POST /api/animal-addendum/:occupancyId/resend-intake    → ResendAnimalAddendumIntakeResult
//
//   • PUBLIC, token-gated (NO Bearer) — the tenant-facing form at
//     /animal-addendum/:token. The token in the path is the only credential, so
//     these go straight to `fetch` like lib/intakeClient.ts.
//       - GET  /api/animal-addendum/intake/:token        → AnimalAddendumIntakeView
//       - POST /api/animal-addendum/intake/:token/photo  → AnimalAddendumPhotoResult (multipart)
//       - POST /api/animal-addendum/intake/:token        → AnimalAddendumSubmitResult
//
// The `:occupancyId` path key is the tenancy's AppFolio occupancy id — the
// deposit-agreement convention, so the intake record and the signature record
// share a group and Cancel / Resend work on either phase. It is an ADDRESS, and
// never reaches the screen (docs/no-ids-in-ui.md).
//
// 503 handling follows depositAgreementClient: a read degrades to
// `sendEnabled: false` rather than throwing (the page shows a read-only notice),
// and a WRITE throws AnimalAddendumNotEnabledError so the page can say why.

import { api, ApiError } from './api';
import type { AnimalAddendumCoveredAnimal, AnimalAddendumSubmission } from './animalAddendumForm';

// Re-export the dependency-free form helpers so call sites import from one
// module (the helpers live in animalAddendumForm to stay testable without the
// network chain).
export {
  animalAddendumReasons,
  animalNamesSentence,
  asksAddendumPhoto,
  buildAnimalAddendumSubmission,
  canSubmitAnimalAddendum,
  newAnimalAddendumAnimal,
  newAnimalAddendumDraft,
  newAnimalId,
} from './animalAddendumForm';
export {
  coveredAnimalDrafts,
  removeAnimalFromDraft,
} from './animalAddendumForm';
export type {
  AnimalAddendumAnimalType,
  AnimalAddendumCoveredAnimal,
  AnimalAddendumDraft,
  AnimalAddendumDraftAnimal,
  AnimalAddendumPhoto,
  AnimalAddendumSubmission,
  AnimalAddendumSubmissionAnimal,
} from './animalAddendumForm';

// `env?.`: under the node test runner (tsx) `import.meta.env` is undefined, and a bare read here
// failed every suite that imports this module at load (ItemPage, noIdsRendered).
const API_BASE =
  import.meta.env?.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** One animal AppFolio already has on the occupancy (the snapshot at initiate). */
export interface AnimalAddendumKnownAnimal {
  name: string;
  /** "Dog" / "Cat" / whatever AppFolio calls it — already display-ready. */
  typeLabel: string;
  age?: string;
  weightLbs?: number;
}

/** A resident or co-signer on the tenancy. */
export interface AnimalAddendumParty {
  /** AppFolio tenant id — addressing only; POSTed back on initiate. */
  tenantId: string;
  name: string;
  /** Split name for the first/last/email row layout (WEL-161); absent on old servers. */
  firstName?: string;
  lastName?: string;
  email: string | null;
}

/** Which roster a recipient came from (the document numbers both as ResidentN). */
export type AnimalAddendumRecipientKind = 'resident' | 'cosigner';

// ---------------------------------------------------------------------------
// AUTHENTICATED staff: the Send page
// ---------------------------------------------------------------------------

/** What a prior round of this addendum is doing right now, if there is one. */
export interface AnimalAddendumPriorSend {
  status: 'initiated' | 'locked' | 'sent';
  round: number;
  initiatedAt: string;
  lockedAt?: string;
  agreementSentAt?: string;
  cancelledAt?: string;
  /** Already-worded signing progress ("Signing 1/3", "Fully Executed"), or null. */
  agreementStatus?: string | null;
  /**
   * Whether anything is still LIVE — the one fact the Send page branches on.
   * Computed server-side on purpose: "finished" is not just fully-executed, it
   * is also declined and cancelled, and re-deriving that from `agreementStatus`
   * on the client would miss both and leave the page offering nothing useful.
   */
  agreementOpen?: boolean;
  /** When the prior round finished. Present only once it has. */
  agreementExecutedAt?: string;
}

/** The money staff confirm before anything goes out (all integer cents). */
export interface AnimalAddendumDefaults {
  rentPerNewAnimalCents: number;
  depositPerNewAnimalCents: number;
  /** yyyy-mm-dd — the 1st of next month unless the server says otherwise. */
  rentStartDate: string;
  /** WEL-160: what the tenancy already pays, read from AppFolio (see
   *  `AnimalAddendumContext.currentCharges` for where each number came from). */
  currentRentCents: number;
  currentDepositCents: number;
}

/** Where a prefilled "currently charged / held" number came from (WEL-160). */
export type CurrentChargeSource = 'appfolio' | 'none' | 'unknown' | 'unavailable';

export interface AnimalAddendumChargeLine {
  description: string;
  amountDisplay: string;
  frequency: string | null;
}

export interface AnimalAddendumCurrentCharges {
  rentSource: CurrentChargeSource;
  /** The active monthly animal-rent lines AppFolio carries (summed into the prefill). */
  animalRentLines: AnimalAddendumChargeLine[];
  /** Active animal charges that are not monthly (a one-time Pet Fee) — shown, not counted. */
  nonMonthlyAnimalLines: AnimalAddendumChargeLine[];
  depositSource: CurrentChargeSource;
  /** Every deposit dollar AppFolio holds for the occupancy, for context. */
  depositHeldTotalDisplay: string | null;
  depositAsOf: string | null;
  /** AppFolio's animal share came back negative (a refund against a non-animal line) — check the ledger. */
  animalDepositNegative?: boolean;
}

/** GET /api/animal-addendum/context — everything the Send page needs. */
export interface AnimalAddendumContext {
  sendEnabled: boolean;
  /** Null when the unit has no current tenancy — the page says so and won't send. */
  occupancyId: string | null;
  propertyAddress: string;
  leaseStartDate: string | null;
  residents: AnimalAddendumParty[];
  cosigners: AnimalAddendumParty[];
  currentAnimals: AnimalAddendumKnownAnimal[];
  defaults: AnimalAddendumDefaults;
  currentCharges: AnimalAddendumCurrentCharges;
  priorSend: AnimalAddendumPriorSend | null;
  /** True only when the service answered 503 — the page degrades read-only. */
  notEnabled: boolean;
}

const EMPTY_DEFAULTS: AnimalAddendumDefaults = {
  rentPerNewAnimalCents: 5000,
  depositPerNewAnimalCents: 50000,
  rentStartDate: '',
  currentRentCents: 0,
  currentDepositCents: 0,
};

const UNAVAILABLE_CHARGES: AnimalAddendumCurrentCharges = {
  rentSource: 'unavailable',
  animalRentLines: [],
  nonMonthlyAnimalLines: [],
  depositSource: 'unavailable',
  depositHeldTotalDisplay: null,
  depositAsOf: null,
};

/**
 * Load the tenancy context for one property + unit.
 *
 * A 503 (feature dormant / mirror down) resolves with `notEnabled: true` and an
 * empty context rather than throwing — the page renders its "not turned on yet"
 * state instead of an error, exactly as listDepositOccupancies does.
 */
export async function getAnimalAddendumContext(
  propertyId: string,
  unitId: string,
): Promise<AnimalAddendumContext> {
  const qs = new URLSearchParams({ propertyId, unitId }).toString();
  try {
    const raw = await api.get<Partial<AnimalAddendumContext>>(
      `/api/animal-addendum/context?${qs}`,
    );
    return {
      sendEnabled: raw.sendEnabled ?? false,
      occupancyId: raw.occupancyId ?? null,
      propertyAddress: raw.propertyAddress ?? '',
      leaseStartDate: raw.leaseStartDate ?? null,
      residents: raw.residents ?? [],
      cosigners: raw.cosigners ?? [],
      currentAnimals: raw.currentAnimals ?? [],
      defaults: { ...EMPTY_DEFAULTS, ...(raw.defaults ?? {}) },
      currentCharges: { ...UNAVAILABLE_CHARGES, ...(raw.currentCharges ?? {}) },
      priorSend: raw.priorSend ?? null,
      notEnabled: false,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      return {
        sendEnabled: false,
        occupancyId: null,
        propertyAddress: '',
        leaseStartDate: null,
        residents: [],
        cosigners: [],
        currentAnimals: [],
        defaults: EMPTY_DEFAULTS,
        currentCharges: UNAVAILABLE_CHARGES,
        priorSend: null,
        notEnabled: true,
      };
    }
    throw err;
  }
}

/** One person the form link goes to. Every entry must carry an email. */
export interface AnimalAddendumInitiateRecipient {
  /** Absent for a signer staff typed in by hand (WEL-161). */
  tenantId?: string;
  name: string;
  email: string;
  kind: AnimalAddendumRecipientKind;
}

/** The four money fields staff confirm (integer cents). */
export interface AnimalAddendumFees {
  currentRentCents: number;
  currentDepositCents: number;
  rentPerNewAnimalCents: number;
  depositPerNewAnimalCents: number;
}

/** WEL-182: one animal staff mark "RRA or pending RRA" (mirrors the API's
 *  AnimalAddendumAccommodationAnimalInput). 'pending' requires the if-not-approved amounts. */
export interface AnimalAddendumAccommodationAnimalInput {
  name: string;
  type: 'dog' | 'cat' | 'other';
  otherKind?: AnimalAddendumCoveredAnimal['otherKind'];
  breed?: string;
  decision: 'approved' | 'pending';
  ifNotApprovedRentCents?: number;
  ifNotApprovedDepositCents?: number;
}

export interface InitiateAnimalAddendumBody {
  propertyId: string;
  unitId: string;
  recipients: AnimalAddendumInitiateRecipient[];
  fees: AnimalAddendumFees;
  /** yyyy-mm-dd. */
  rentStartDate: string;
  /** Send-claim UX: true re-sends over a prior fully-executed round. */
  confirmNewSend?: boolean;
  /** WEL-182: the animals marked "RRA or pending RRA" (at most 10). */
  accommodationAnimals?: AnimalAddendumAccommodationAnimalInput[];
}

export interface InitiateAnimalAddendumResult {
  ok: boolean;
  occupancyId: string;
  round: number;
  /** How many form links actually went out. */
  recipientsEmailed: number;
}

/**
 * Thrown when a write answers 503 — the feature is dormant
 * (`ANIMAL_ADDENDUM_SEND_ENABLED=false`, or WHP messaging off). Mirrors
 * LeaseholderChangeNotEnabledError so the page can render the dormant state.
 */
export class AnimalAddendumNotEnabledError extends Error {
  constructor(message = 'Animal addendum sends are not enabled right now.') {
    super(message);
    this.name = 'AnimalAddendumNotEnabledError';
  }
}

/**
 * Thrown on 409 `prior_send_open`. Two different situations, told apart by
 * `priorFullyExecuted`:
 *
 *   false — something is still LIVE: the form out, a completed round whose
 *           addendum has not gone yet, one out for signature, or a parked
 *           DocuSeal create. Cancel or Resend form is what unsticks all four.
 *   true  — the prior round finished signing. A second addendum is allowed,
 *           behind the confirmation; the page has simply not learned the round
 *           finished yet.
 *
 * Declined and cancelled priors raise neither: they duplicate nothing, so a new
 * round just succeeds.
 *
 * The flag is read off THIS BODY rather than re-derived from the loaded
 * context, and that is load-bearing. The page can have loaded while the
 * addendum was still signing and the last signature landed a moment later — no
 * client-side guard can fire on state the client does not have yet, so the
 * server's answer is the fresh one and it has to win.
 */
export class AnimalAddendumPriorSendOpenError extends Error {
  constructor(
    message = 'A form or agreement is already out for this tenancy.',
    public priorFullyExecuted = false,
  ) {
    super(message);
    this.name = 'AnimalAddendumPriorSendOpenError';
  }
}

/** The `error` string a route put in its JSON body, when it sent one. */
export function animalAddendumErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : fallback;
}

function rethrowWrite(err: unknown): never {
  if (err instanceof ApiError && err.status === 503) {
    throw new AnimalAddendumNotEnabledError(
      animalAddendumErrorMessage(err, 'Animal addendum sends are not enabled right now.'),
    );
  }
  if (err instanceof ApiError && err.status === 409) {
    const body = err.body as {
      code?: string;
      error?: string;
      priorFullyExecuted?: boolean;
    } | null;
    if (body?.code === 'prior_send_open') {
      throw new AnimalAddendumPriorSendOpenError(
        body.error ?? 'A form or agreement is already out for this tenancy.',
        body.priorFullyExecuted === true,
      );
    }
  }
  throw err;
}

/**
 * Send the animal form to every recipient on the tenancy. The addendum itself
 * goes out for signature automatically once the first person completes it.
 */
export async function initiateAnimalAddendum(
  occupancyId: string,
  body: InitiateAnimalAddendumBody,
): Promise<InitiateAnimalAddendumResult> {
  try {
    const raw = await api.post<Partial<InitiateAnimalAddendumResult>>(
      `/api/animal-addendum/${encodeURIComponent(occupancyId)}/initiate`,
      body,
    );
    return {
      ok: raw.ok ?? true,
      occupancyId: raw.occupancyId ?? occupancyId,
      round: raw.round ?? 1,
      recipientsEmailed: raw.recipientsEmailed ?? 0,
    };
  } catch (err) {
    return rethrowWrite(err);
  }
}

export interface CancelAnimalAddendumResult {
  cancelled: boolean;
  /** DocuSeal submission voided, or null when nothing was out for signing. */
  voidedSubmissionId: number | null;
  recipientsEmailed: number;
  /** True when it was already cancelled (idempotent no-op). */
  alreadyCancelled?: boolean;
}

/**
 * Cancel the whole request at any stage: close the form links, void the
 * agreement if one is out for signing, and email every recipient that there is
 * nothing further to do. The tenancy stays sendable afterwards.
 */
export async function cancelAnimalAddendum(
  occupancyId: string,
): Promise<CancelAnimalAddendumResult> {
  try {
    const raw = await api.post<{
      cancelled?: boolean;
      voidedSubmissionId?: number | null;
      recipientsEmailed?: number;
      alreadyCancelled?: boolean;
    }>(`/api/animal-addendum/${encodeURIComponent(occupancyId)}/cancel`);
    return {
      cancelled: raw.cancelled ?? false,
      voidedSubmissionId: raw.voidedSubmissionId ?? null,
      recipientsEmailed: raw.recipientsEmailed ?? 0,
      ...(raw.alreadyCancelled !== undefined ? { alreadyCancelled: raw.alreadyCancelled } : {}),
    };
  } catch (err) {
    return rethrowWrite(err);
  }
}

export interface ResendAnimalAddendumIntakeResult {
  ok: boolean;
  round: number;
  /** How many form links went back out after the void + restart. */
  recipientsEmailed: number;
}

/**
 * Void the open agreement (if any) and restart the animal form from scratch.
 * The first completion of the restarted form re-locks the answers.
 */
export async function resendAnimalAddendumIntake(
  occupancyId: string,
): Promise<ResendAnimalAddendumIntakeResult> {
  try {
    const raw = await api.post<{ ok?: boolean; round?: number; recipientsEmailed?: number }>(
      `/api/animal-addendum/${encodeURIComponent(occupancyId)}/resend-intake`,
    );
    return {
      ok: raw.ok ?? true,
      round: raw.round ?? 0,
      recipientsEmailed: raw.recipientsEmailed ?? 0,
    };
  } catch (err) {
    return rethrowWrite(err);
  }
}

// ---------------------------------------------------------------------------
// PUBLIC token-gated form
// ---------------------------------------------------------------------------

export class AnimalAddendumApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`Animal addendum API error ${status}`);
  }
}

/**
 * The form's lifecycle, entirely server-driven:
 *   • 'fillable'    — open; this person can tell us about the animal.
 *   • 'locked'      — somebody in the household completed it first.
 *   • 'sent'        — completed AND the addendum is already out for signature.
 *   • 'unavailable' — bad/expired/foreign token, dormant flag, or cancelled.
 *                     Deliberately neutral: it never says which.
 */
export type AnimalAddendumIntakeState = 'fillable' | 'locked' | 'sent' | 'unavailable';

/** The money sentence, pre-formatted by the server so every surface says it identically. */
export interface AnimalAddendumTerms {
  rentPerNewAnimalDisplay: string;
  depositPerNewAnimalDisplay: string;
  rentStartDisplay: string;
}

/** GET /api/animal-addendum/intake/:token. */
export interface AnimalAddendumIntakeView {
  state: AnimalAddendumIntakeState;
  premises: string;
  /** Who this link belongs to. `key` is addressing only; `name` is what shows. */
  recipient: { key: string; name: string } | null;
  household: Array<{ name: string; kind: AnimalAddendumRecipientKind }>;
  currentAnimals: AnimalAddendumKnownAnimal[];
  /**
   * RRA v2.2 part B: animals this round covers at no additional animal rent or deposit — pre-listed on
   * the form (what the server knows prefilled; details to complete), not removable, submitted under the
   * same `id`. The server omits it on every other view; it is `[]` here then. Nothing says why. The
   * "{Name} — no additional animal rent or deposit." line is the terms block's, not the card's — and so
   * is a pending animal's ("{Name} — $0 for now. If we don't waive it, …", WEL-182, `pendingDecision`).
   */
  coveredAnimals: AnimalAddendumCoveredAnimal[];
  terms: AnimalAddendumTerms;
  alreadyLocked: boolean;
  /** locked/sent: who completed it (a first name is enough). */
  lockedByName?: string;
}

const EMPTY_TERMS: AnimalAddendumTerms = {
  rentPerNewAnimalDisplay: '',
  depositPerNewAnimalDisplay: '',
  rentStartDisplay: '',
};

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData sets its own multipart boundary — never override it.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty or non-JSON body */
    }
    throw new AnimalAddendumApiError(res.status, body);
  }
  return (await res.json()) as T;
}

function normalizeView(raw: Partial<AnimalAddendumIntakeView>): AnimalAddendumIntakeView {
  return {
    state: raw.state ?? 'unavailable',
    premises: raw.premises ?? '',
    recipient: raw.recipient ?? null,
    household: raw.household ?? [],
    currentAnimals: raw.currentAnimals ?? [],
    coveredAnimals: raw.coveredAnimals ?? [],
    terms: { ...EMPTY_TERMS, ...(raw.terms ?? {}) },
    alreadyLocked: raw.alreadyLocked ?? false,
    ...(raw.lockedByName ? { lockedByName: raw.lockedByName } : {}),
  };
}

/** GET the public, token-gated form view. */
export async function getAnimalAddendumIntake(
  token: string,
): Promise<AnimalAddendumIntakeView> {
  const raw = await publicRequest<Partial<AnimalAddendumIntakeView>>(
    `/api/animal-addendum/intake/${encodeURIComponent(token)}`,
  );
  return normalizeView(raw);
}

export interface AnimalAddendumSubmitResult {
  ok: boolean;
  /** 'sent' when this submission won and the addendum went out; 'locked' when beaten. */
  state: AnimalAddendumIntakeState;
  alreadyLocked: boolean;
  docusealSubmissionId?: number;
}

/** POST the new animal(s). The winner's submission sends the addendum for signature. */
export async function submitAnimalAddendumIntake(
  token: string,
  body: AnimalAddendumSubmission,
): Promise<AnimalAddendumSubmitResult> {
  const raw = await publicRequest<Partial<AnimalAddendumSubmitResult>>(
    `/api/animal-addendum/intake/${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return {
    ok: raw.ok ?? true,
    state: raw.state ?? 'sent',
    alreadyLocked: raw.alreadyLocked ?? false,
    ...(raw.docusealSubmissionId !== undefined
      ? { docusealSubmissionId: raw.docusealSubmissionId }
      : {}),
  };
}

/** POST /api/animal-addendum/intake/:token/photo — one image for one animal. */
export interface AnimalAddendumPhotoResult {
  ok: boolean;
  photo: {
    id: string;
    animalId: string;
    contentType: string;
    bytes: number;
    /** Presigned GET, good for five minutes — the form's thumbnail. */
    previewUrl: string;
  };
}

/**
 * Upload one animal's photo. Multipart: the file under `photo`, the animal's
 * client id under `animalId`. A photo for an animal that never makes it into
 * the submission is simply ignored server-side, so a failed or abandoned upload
 * never blocks the form.
 *
 * 415 = not an image; 413 = over 10 MB. Both surface as an inline note.
 */
export async function uploadAnimalAddendumPhoto(
  token: string,
  animalId: string,
  file: File,
): Promise<AnimalAddendumPhotoResult> {
  const form = new FormData();
  form.append('animalId', animalId);
  form.append('photo', file);
  const raw = await publicRequest<Partial<AnimalAddendumPhotoResult>>(
    `/api/animal-addendum/intake/${encodeURIComponent(token)}/photo`,
    { method: 'POST', body: form },
  );
  return {
    ok: raw.ok ?? true,
    photo: {
      id: raw.photo?.id ?? '',
      animalId: raw.photo?.animalId ?? animalId,
      contentType: raw.photo?.contentType ?? file.type,
      bytes: raw.photo?.bytes ?? file.size,
      previewUrl: raw.photo?.previewUrl ?? '',
    },
  };
}

/** A friendly, non-blocking message for a failed photo upload. */
export function photoUploadMessage(err: unknown): string {
  if (err instanceof AnimalAddendumApiError) {
    if (err.status === 413) return 'That photo is too large — please pick one under 10 MB.';
    if (err.status === 415) return 'That file isn’t an image — please pick a photo.';
  }
  return 'We couldn’t attach that photo. You can try again, or just submit without one.';
}
